/**
 * Settings -> Notifications -> Email templates, and Email branding (attendance emails plan PR 4).
 *
 * A company rewords any notification's EMAIL in a formatting editor. The editor sends a structured
 * document (`emailDocSchema`, see notifications/email-doc.ts) — never HTML — and the server renders
 * it, escaping all typed text. Only the variables the notification actually has can be used.
 * Account-security emails (password reset, verification, MFA) cannot be changed.
 *
 * Branding wraps EVERY email: the company logo (or name), a brand colour, a footer line.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { routeMeta } from './meta.js';
import { errorBodySchema, instantSchema } from '../schemas/common.js';
import { EMAIL_DOC_LIMITS, emailDocSchema } from '../notifications/email-doc.js';

const c = initContract();

const eventKeyParam = z.object({ eventKey: z.string().trim().min(1).max(120) });

export const emailTemplateListItemSchema = z.object({
  eventKey: z.string(),
  module: z.string(),
  displayName: z.string(),
  description: z.string(),
  customised: z.boolean(),
  updatedAt: instantSchema.nullable(),
});
export type EmailTemplateListItem = z.infer<typeof emailTemplateListItemSchema>;

export const emailTemplateContentSchema = z.object({
  subject: z.string(),
  doc: emailDocSchema,
});
export type EmailTemplateContent = z.infer<typeof emailTemplateContentSchema>;

export const emailTemplateDetailSchema = z.object({
  eventKey: z.string(),
  module: z.string(),
  displayName: z.string(),
  description: z.string(),
  /** Variables this email may use, with a sample value for the preview. */
  variables: z.array(z.object({ name: z.string(), sample: z.string() })),
  /** Variables that are whole blocks (e.g. the task table): insert them on a line of their own. */
  htmlBlocks: z.array(z.string()),
  /** The platform's wording. */
  default: emailTemplateContentSchema,
  /** The company's wording, or null when it uses the default. */
  custom: emailTemplateContentSchema
    .extend({ updatedAt: instantSchema, updatedByName: z.string().nullable() })
    .nullable(),
});
export type EmailTemplateDetail = z.infer<typeof emailTemplateDetailSchema>;

export const saveEmailTemplateBodySchema = z.object({
  subject: z.string().trim().min(1).max(EMAIL_DOC_LIMITS.maxSubject),
  doc: emailDocSchema,
});
export type SaveEmailTemplateBody = z.infer<typeof saveEmailTemplateBodySchema>;

export const emailTemplatePreviewSchema = z.object({
  subject: z.string(),
  /** The full email as it will look, branding included, filled with sample values. */
  html: z.string(),
});
export type EmailTemplatePreview = z.infer<typeof emailTemplatePreviewSchema>;

export const emailTemplateTestResultSchema = z.object({
  status: z.enum(['success', 'failed']),
  error: z.string().nullable(),
  sentTo: z.string(),
});

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

export const emailBrandingSchema = z.object({
  showLogo: z.boolean(),
  /** `#RRGGBB`, or null for the organisation's accent colour. */
  brandColor: z.string().regex(HEX_COLOR, 'Use a colour like #1D4ED8').nullable(),
  footerText: z.string().trim().max(500).nullable(),
});
export type EmailBrandingSettings = z.infer<typeof emailBrandingSchema>;

export const emailBrandingResponseSchema = emailBrandingSchema.extend({
  /** The organisation's logo, if it has one (Company Profile). Shown when `showLogo`. */
  logoUrl: z.string().nullable(),
  orgName: z.string(),
  /** The footer used when `footerText` is empty. */
  defaultFooter: z.string(),
  customised: z.boolean(),
  updatedAt: instantSchema.nullable(),
});
export type EmailBrandingResponse = z.infer<typeof emailBrandingResponseSchema>;

const READ = routeMeta(['notification.config:read', 'notification.config:manage']);
const MANAGE = routeMeta('notification.config:manage');

export const emailTemplatesContract = c.router(
  {
    listTemplates: {
      method: 'GET',
      path: '/email-templates',
      responses: {
        200: z.object({ items: z.array(emailTemplateListItemSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Every email a company can reword, and whether it has',
      metadata: READ,
    },
    getTemplate: {
      method: 'GET',
      path: '/email-templates/:eventKey',
      pathParams: eventKeyParam,
      responses: {
        200: emailTemplateDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "One email's default and company wording, its variables and sample values",
      metadata: READ,
    },
    saveTemplate: {
      method: 'PUT',
      path: '/email-templates/:eventKey',
      pathParams: eventKeyParam,
      body: saveEmailTemplateBodySchema,
      responses: {
        200: emailTemplateDetailSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: "Save the company's wording for one email",
      metadata: MANAGE,
    },
    resetTemplate: {
      method: 'DELETE',
      path: '/email-templates/:eventKey',
      pathParams: eventKeyParam,
      body: z.object({}).strict().optional(),
      responses: {
        200: emailTemplateDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Go back to the default wording',
      metadata: MANAGE,
    },
    previewTemplate: {
      method: 'POST',
      path: '/email-templates/:eventKey/preview',
      pathParams: eventKeyParam,
      body: saveEmailTemplateBodySchema,
      responses: {
        200: emailTemplatePreviewSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Render a draft with sample values and the company branding',
      metadata: READ,
    },
    testTemplate: {
      method: 'POST',
      path: '/email-templates/:eventKey/test',
      pathParams: eventKeyParam,
      body: saveEmailTemplateBodySchema,
      responses: {
        200: emailTemplateTestResultSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
        429: errorBodySchema,
      },
      summary: "Send a draft, filled with sample values, to the caller's own email address",
      metadata: MANAGE,
    },
    getBranding: {
      method: 'GET',
      path: '/email-branding',
      responses: { 200: emailBrandingResponseSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary: 'How every email from the company looks (logo, colour, footer)',
      metadata: READ,
    },
    saveBranding: {
      method: 'PUT',
      path: '/email-branding',
      body: emailBrandingSchema,
      responses: {
        200: emailBrandingResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Set the logo, colour and footer used on every email',
      metadata: MANAGE,
    },
  },
  { pathPrefix: '/notification-settings' },
);
